import { createClient, type Client } from "@connectrpc/connect";
import type { DescService } from "@bufbuild/protobuf";
import { binaryTransport, transport } from "./client";

/**
 * Type-safe client factory for protobuf-es v2 / connectrpc v2.
 *
 * This wrapper centralizes the type coercion needed because GenService<T>
 * (from @bufbuild/protobuf/codegenv2) has branded types that are structurally
 * compatible with DescService but not directly assignable due to nominal typing.
 *
 * The function accepts a GenService and returns a properly typed Client<T>.
 * The type assertion happens in one place rather than at every service instantiation.
 */
export function createTypedClient<T extends DescService>(service: T): Client<T> {
  // GenService<T> is structurally compatible with DescService but has additional
  // branded properties. The cast to DescService is safe because:
  // 1. GenService extends Omit<DescService, "method"> & { method: ... }
  // 2. The method property is structurally compatible with Record<string, DescMethod>
  // 3. All other DescService properties are preserved
  return createClient(service as DescService as T, transport);
}

/**
 * As createTypedClient, but over the protobuf-binary transport.
 *
 * Use this for a service whose messages carry a `bytes` payload of any size —
 * today that is the blob store, which streams source documents to the reviewer.
 * Connect's JSON default base64-encodes bytes, inflating the transfer by a
 * third and forcing a JSON.parse plus base64 decode per chunk on the main
 * thread. See `binaryTransport` in ./client for why this is opt-in per service
 * rather than the global default.
 */
export function createBinaryClient<T extends DescService>(service: T): Client<T> {
  return createClient(service as DescService as T, binaryTransport);
}
